/** Id of the static boot screen markup in `public/index.html`. */
const BOOT_ELEMENT_ID = 'fb-boot'

/** Must match the `transition: opacity` duration on `#fb-boot`. */
const FADE_MS = 200

/**
 * Class on `<html>` that fills the canvas with the boot screen's background
 * before any stylesheet arrives. See the comment beside its rule in
 * `public/index.html` for why it cannot simply live on `html` for good.
 */
const BOOT_ROOT_CLASS = 'fb-booting'

/**
 * Takes down the static boot screen that `public/index.html` paints before any
 * JavaScript runs.
 *
 * Called from an effect, never during render: by the time an effect fires React
 * has committed *and* painted its own first screen underneath, which on every
 * route is either `components/Preloader` or real content — never an empty page.
 * Removing it any earlier (a `requestAnimationFrame` after `root.render`, say)
 * would race the first commit and reintroduce the blank frames this exists to
 * prevent.
 *
 * The two screens are drawn to match, so the fade is only covering the webfont
 * swap between the boot screen's monospace fallback and `Courier Prime 2`.
 *
 * The canvas fill goes at the same moment the fade starts, not when it ends, so
 * what the boot screen fades away to is already the theme's own background
 * rather than a dark canvas that would snap white a frame later.
 *
 * Idempotent, and a no-op in a document that never had the element (unit tests,
 * the editor's iframes).
 */
export const dismissBootPreloader = () => {
  // eslint-disable-next-line no-undef
  const element = typeof document === 'undefined' ? null : document.getElementById(BOOT_ELEMENT_ID)
  if (!element || element.hasAttribute('data-dismissed')) return

  element.setAttribute('data-dismissed', '')
  // eslint-disable-next-line no-undef
  document.documentElement.classList.remove(BOOT_ROOT_CLASS)
  setTimeout(() => element.remove(), FADE_MS)
}

/**
 * Takes the boot screen down at once, with no fade.
 *
 * For the headless PDF render route, where a screenshot must never catch the
 * boot overlay or its fade. Safe after `dismissBootPreloader` (removing an
 * element twice is a no-op).
 */
export const removeBootPreloader = () => {
  // eslint-disable-next-line no-undef
  if (typeof document === 'undefined') return
  // eslint-disable-next-line no-undef
  document.getElementById(BOOT_ELEMENT_ID)?.remove()
  // eslint-disable-next-line no-undef
  document.documentElement.classList.remove(BOOT_ROOT_CLASS)
}
